import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Image as ImageIcon } from "lucide-react";
import {
  notificationPreviewApi,
  notificationTemplateToolsApi,
  type MessagingTemplate,
  type NotificationEventRow,
} from "../../shared/api/notificationSettings";
import { meApi, meQueryKeys } from "../../shared/api/me";
import { apiError } from "../../shared/api/client";
import type { useTemplatePreview } from "./ParamTable";

/** How long the Zalo preview may take before the screen stops waiting for it. */
const IFRAME_TIMEOUT_MS = 6000;

/** "•••667" — the last three digits are enough to recognise one's own number. */
export function maskPhone(phone: string | null | undefined): string | null {
  const digits = (phone ?? "").replace(/\D/g, "");
  return digits.length >= 3 ? `•••${digits.slice(-3)}` : null;
}

/** Bounds on a reported height: a page that reports 0 or 50 000 is not a template. */
const MIN_HEIGHT = 120;
const MAX_HEIGHT = 1200;

/**
 * Lines added to Zalo's page so it says how tall it rendered (#19605): the whole document — the
 * template container sits in a 12px margin, and its own bottom alone cuts the card's foot off —
 * on load and whenever it resizes — Zalo's own renderer sets its inner frame's height
 * after the fact, which is a resize. `<base>` keeps the page's relative addresses on Zalo, and
 * the height override stops a full-viewport body from measuring the frame instead of the content.
 */
const SIZE_REPORTER = `<base href="https://account.zalo.cloud/">
<style>html,body{height:auto!important;min-height:0!important;overflow:hidden!important}</style>
<script>(function(){var last=0;
function report(){var c=document.querySelector('.preview-container')||document.body;
var m=parseFloat(getComputedStyle(c).marginBottom||'0');
var h=Math.ceil(Math.max(document.documentElement.getBoundingClientRect().height,
c.getBoundingClientRect().bottom+window.scrollY+m));
if(h>0&&h!==last){last=h;parent.postMessage({__hisZaloHeight:h},'*');}}
addEventListener('load',report);
document.addEventListener('DOMContentLoaded',function(){report();
if(window.ResizeObserver)new ResizeObserver(report).observe(document.querySelector('.preview-container')||document.body);});
})();</script>`;

/**
 * Zalo's page as a `data:` URL, with the size reporter (#19605). A `data:` document has an opaque
 * origin of its own: even with `allow-same-origin` — which Zalo's renderer needs, it writes into a
 * frame it creates — it can read nothing of HIS, and HIS can read nothing of it.
 */
export function framedPage(html: string): string {
  const at = html.search(/<head[^>]*>/i);
  const withReporter =
    at < 0
      ? SIZE_REPORTER + html
      : html.replace(/<head[^>]*>/i, (m) => m + SIZE_REPORTER);
  const bytes = new TextEncoder().encode(withReporter);
  let bin = "";
  for (let k = 0; k < bytes.length; k += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(k, k + 0x8000));
  }
  return `data:text/html;charset=utf-8;base64,${btoa(bin)}`;
}

/**
 * The Zalo preview (#19587, #19605). Zalo's own page, not a published API, so there is always a
 * way out: if the page cannot be fetched or never reports a height in time, the frame gives way to
 * a link. Sized to what Zalo rendered — templates differ in height by their parameters and their
 * text, and a fixed height either cuts one or leaves a grey block under another.
 */
function ZaloFrame({ url }: { url: string }) {
  const page = useQuery({
    queryKey: ["zalo-preview-page", url],
    queryFn: () => notificationPreviewApi.zaloPage(url),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const src = useMemo(() => (page.data ? framedPage(page.data) : null), [page.data]);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setHeight(null);
    setTimedOut(false);
    if (!src) return;
    const onMessage = (e: MessageEvent) => {
      // Only this frame's own report, and only a number in range.
      if (e.source !== frameRef.current?.contentWindow) return;
      const h = Number((e.data as { __hisZaloHeight?: unknown } | null)?.__hisZaloHeight);
      if (Number.isFinite(h) && h > 0) setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, h)));
    };
    window.addEventListener("message", onMessage);
    const t = window.setTimeout(() => setTimedOut(true), IFRAME_TIMEOUT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(t);
    };
  }, [src]);

  const failed = page.isError || (timedOut && height === null);
  return (
    <div className="grid gap-1">
      {!failed && !src && (
        <small className="text-xs text-muted-foreground">Đang tải hình ảnh mẫu…</small>
      )}
      {!failed && src && (
        <iframe
          ref={frameRef}
          src={src}
          title="Mẫu tin trên Zalo"
          sandbox="allow-scripts allow-same-origin"
          referrerPolicy="no-referrer"
          scrolling="no"
          // Hidden until it has said its size, so it never shows at the wrong one first.
          // + the 1px border top and bottom: the box is border-box, the reported height is the page's.
          style={{
            height: (height ?? MIN_HEIGHT) + 2,
            visibility: height === null ? "hidden" : "visible",
          }}
          className="w-full max-w-[400px] rounded-md border bg-[#efefef]"
          data-testid="zalo-frame"
        />
      )}
      {/* #19597 — the link only when the picture did not load: next to a picture it shows the
          same thing twice. */}
      {failed && (
        <small className="text-xs text-muted-foreground">
          Không hiển thị được mẫu ngay tại đây.{" "}
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-action underline-offset-2 hover:underline"
          >
            Mở trên Zalo ↗
          </a>
        </small>
      )}
    </div>
  );
}

/**
 * "Bệnh nhân nhận" (#19587) — what the patient gets, next to the configuration that produces it:
 * Zalo's own rendering of the template, and the test send as the last step of a check. The value of
 * each slot is in "Tham số của mẫu" beside it, and only there (#19597).
 */
export function PatientReceives({
  event,
  serviceId,
  template,
  query,
  draft,
}: {
  event: NotificationEventRow;
  serviceId: string | null;
  template: MessagingTemplate | undefined;
  query: ReturnType<typeof useTemplatePreview>;
  /** An unsaved draft: the server tests what is SAVED, so the button waits for a save. */
  draft: boolean;
}) {
  // #19594 — folded on every screen until asked: the configuration comes first. The folded state
  // is a visible button with an image icon, not a grey caption, so it reads as "there is a picture
  // behind this".
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const me = useQuery({
    queryKey: meQueryKeys.profile(),
    queryFn: () => meApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const masked = maskPhone(me.data?.phone);
  const testSend = useMutation({
    mutationFn: () =>
      notificationTemplateToolsApi.testSend(event.eventKey, { channel: "ZBS", serviceId }),
    onSuccess: (r) => {
      const at = new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
      setResult({ ok: true, text: `Đã gửi lúc ${at} tới ${maskPhone(r.sentTo) ?? r.sentTo}.` });
    },
    onError: (e) => setResult({ ok: false, text: apiError(e).detail }),
  });

  const url =
    query.data?.previewImageUrl ??
    template?.renditions.find((r) => r.channel === "ZBS")?.previewImageUrl ??
    null;
  const disabledReason = draft
    ? "Lưu thay đổi để gửi thử."
    : !me.isLoading && !masked
      ? "Tài khoản của bạn chưa có số điện thoại."
      : null;

  return (
    <div className="grid content-start gap-2" data-testid="patient-receives">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Bệnh nhân nhận
      </span>
      {open ? (
        <button
          type="button"
          className="flex items-center gap-1 justify-self-start text-xs text-action underline-offset-2 hover:underline"
          aria-expanded
          onClick={() => setOpen(false)}
        >
          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          Thu gọn hình ảnh
        </button>
      ) : (
        <button
          type="button"
          className="flex w-full max-w-[400px] items-center gap-3 rounded-md border border-dashed bg-muted/30 px-3 py-2.5 text-left hover:border-action hover:bg-muted/60"
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          <ImageIcon className="h-6 w-6 shrink-0 text-action" aria-hidden />
          <span className="grid min-w-0 flex-1">
            <span className="text-sm font-medium text-action">Xem hình ảnh tin nhắn</span>
            <span className="text-xs text-muted-foreground">
              {url ? "Tin bệnh nhân nhận trên Zalo trông như thế nào" : "Mẫu chưa có ảnh xem trước trên Zalo"}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      )}
      {open && (
        <>
          {url ? (
            <ZaloFrame url={url} />
          ) : (
            <small className="text-xs text-muted-foreground">Mẫu chưa có ảnh xem trước trên Zalo.</small>
          )}
        </>
      )}
      <div className="grid gap-1">
        <button
          type="button"
          className="h-8 justify-self-start rounded-md border px-3 text-xs font-medium text-action hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!!disabledReason || testSend.isPending || me.isLoading}
          onClick={() => {
            setResult(null);
            testSend.mutate();
          }}
        >
          {testSend.isPending ? "Đang gửi thử…" : `Gửi thử tới Zalo ${masked ?? ""}`.trim()}
        </button>
        {disabledReason ? (
          <small className="text-xs text-muted-foreground" data-testid="test-send-disabled">
            {disabledReason}
            {!draft && (
              <>
                {" "}
                <a href="/me/profile" className="text-action underline">
                  Cập nhật ở Hồ sơ cá nhân
                </a>
              </>
            )}
          </small>
        ) : (
          <small className="text-xs text-muted-foreground">
            {/* #19595 — never "free": a ZBS test send is a real message on the connected OA. */}
            Gửi một tin Zalo thật tới số điện thoại trong{" "}
            <a href="/me/profile" className="text-action underline-offset-2 hover:underline">
              Hồ sơ cá nhân
            </a>{" "}
            của bạn.
            {serviceId ? " Theo cấu hình của dịch vụ này." : ""}
          </small>
        )}
        {result && (
          <small
            role="status"
            className={"text-xs " + (result.ok ? "text-emerald-700" : "text-destructive")}
            data-testid="test-send-result"
          >
            {result.text}
          </small>
        )}
      </div>
    </div>
  );
}
